type CheckboxState = 'checked' | 'unchecked' | 'indeterminate';

function calculateState(checkboxes: HTMLInputElement[]): CheckboxState {
  const checkedCount = checkboxes.filter(cb => cb.checked).length;
  const hasIndeterminate = checkboxes.some(cb => cb.indeterminate);

  if (checkedCount === checkboxes.length && checkboxes.length > 0 && !hasIndeterminate) {
    return 'checked';
  } else if (checkedCount > 0 || hasIndeterminate) {
    return 'indeterminate';
  }
  return 'unchecked';
}

function applyState(checkbox: HTMLInputElement, state: CheckboxState) {
  checkbox.checked = state === 'checked';
  checkbox.indeterminate = state === 'indeterminate';
}

function setAllChecked(checkboxes: HTMLInputElement[], checked: boolean) {
  for (const cb of checkboxes) {
    cb.checked = checked;
    cb.indeterminate = false;
  }
}

/**
 * Enables "toggle all" functionality for a flat list of checkboxes.
 * A master checkbox controls all target checkboxes, and updates its state
 * (checked/indeterminate/unchecked) based on the targets.
 */
export function enableToggleAll(toggleSelector: string, checkBoxSelector: string) {
  const toggle = document.querySelector<HTMLInputElement>(toggleSelector);
  if (!toggle) return;

  const checkboxes = Array.from(document.querySelectorAll<HTMLInputElement>(checkBoxSelector));

  function update() {
    applyState(toggle, calculateState(checkboxes));
  }

  update();

  for (const cb of checkboxes) {
    cb.addEventListener('change', update);
  }

  toggle.addEventListener('change', () => {
    setAllChecked(checkboxes, toggle.checked);
  });
}

/**
 * Enables hierarchical checkbox behavior for a tree structure.
 * Parent checkboxes cascade down to children (one-way).
 * Children never affect parent state.
 */
export function enableHierarchyToggle(containerSelector: string) {
  const container = document.querySelector<HTMLElement>(containerSelector);
  if (!container) return;

  container.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.tagName !== 'INPUT' || target.getAttribute('type') !== 'checkbox') return;

    const checkbox = target as HTMLInputElement;
    const li = checkbox.closest('li');
    if (!li) return;

    const descendantCheckboxes = Array.from(li.querySelectorAll<HTMLInputElement>('input[type=checkbox]'));
    setAllChecked(descendantCheckboxes, checkbox.checked);
  });
}
